<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user.store';
import { UserRoles } from '@/_helpers/interfaces';
import { UserPlus } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { useRouter } from 'vue-router';
import SelectionNeededIllustration from '@/assets/images/selection-needed.svg';

interface Props {
  hasCustomers: boolean; // true = customers exist but none selected, false = no customers at all
}

defineProps<Props>();
const { t } = useI18n();
const router = useRouter();
const { currentUser } = storeToRefs(useUserStore());

const isAdmin = computed(
  () => currentUser.value?.user_role?.includes(UserRoles.ADMIN) || currentUser.value?.is_admin
);

const navigateToCreateCustomer = () => {
  router.push({ name: 'create-customer' });
};
</script>

<template>
  <div
    class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[60vh] tw-p-8 tw-bg-background"
  >
    <!-- Illustration -->
    <SelectionNeededIllustration class="tw-w-full tw-h-auto tw-max-w-[300px] tw-mb-6" />

    <!-- No Customer Selected State -->
    <template v-if="hasCustomers">
      <h2 class="tw-text-xl tw-font-semibold tw-text-foreground tw-mb-2">
        {{ t('customer.empty_state.no_selection_title') }}
      </h2>
      <p class="tw-text-muted-foreground tw-text-center tw-max-w-md">
        {{ t('customer.empty_state.no_selection_description') }}
      </p>
    </template>

    <!-- No Customers Exist State -->
    <template v-else>
      <h2 class="tw-text-xl tw-font-semibold tw-text-foreground tw-mb-2">
        {{ t('customer.empty_state.no_customers_title') }}
      </h2>
      <p class="tw-text-muted-foreground tw-text-center tw-max-w-md tw-mb-6">
        {{ t('customer.empty_state.no_customers_description') }}
      </p>
      <Button v-if="isAdmin" @click="navigateToCreateCustomer">
        <UserPlus class="tw-h-4 tw-w-4 tw-mr-2" />
        {{ t('customer.empty_state.add_customer_button') }}
      </Button>
    </template>
  </div>
</template>
